/**
 * Listagem padrao com acoes por linha (porte de `frontend/lib/crud.py`, spec 0003 secoes B e C).
 *
 * Tres telas numa so, e e isso que distingue de um CRUD generico qualquer:
 *
 * - a COLECAO, numa tabela dentro de um painel com cabecalho e contagem;
 * - o REGISTRO, em pagina inteira (`Registro.tsx`), para ver e para editar;
 * - as CONFIRMACOES (excluir, ressincronizar), que continuam em modal - interromper e o ponto
 *   delas, e sao curtas.
 *
 * Ver e editar deixaram de ser painel lateral porque catorze campos nao cabem numa coluna
 * estreita: a URL de embed saia cortada e o Salvar ficava no fim de uma rolagem longa.
 *
 * Duas regras herdadas do Streamlit que NAO sao detalhe de implementacao:
 *
 * 1. **So os campos alterados sao enviados** no PUT. O backend recusa mudanca de ids do PowerBI
 *    quando ha reports vinculados; mandar o valor igual de volta viraria uma recusa por algo que a
 *    pessoa nem tocou.
 * 2. **A exclusao consulta as dependencias ANTES de tentar.** A regra e do backend e continua la;
 *    isto so evita oferecer um botao que vai falhar. Se a consulta em si falhar, o botao continua
 *    disponivel: quem decide e o servidor.
 */

import { Button, Dialog, IconButton, StatusBadge } from '@ledger-glass/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState, type ReactNode } from 'react'

import {
  atualizar,
  excluir,
  fetchDependencias,
  ressincronizar,
  type Entidade,
} from '../api/catalog'
import { Alert } from '../components/Alert'
import { Spinner } from '../components/Spinner'
import { humanizar, ROTULOS_DE_DEPENDENCIA } from '../errors/humanizar'
import { useMarcarRegistroAberto } from '../shell/registro'
import type { CampoMeta } from './campos'
import { maiuscula, PaginaDeDetalhes, PaginaDeEdicao } from './Registro'

export interface Coluna<T> {
  titulo: string
  valor: (item: T) => ReactNode
}

export interface ListagemProps<T> {
  itens: T[]
  colunas: Array<Coluna<T>>
  entidade: Entidade
  /** Chave da consulta a invalidar depois de cada mutacao. */
  chave: string
  /** Como o registro e chamado em portugues, no singular: "cliente", "dataset". */
  rotulo: string
  /** Plural, para o cabecalho do painel e para o caminho de volta. Ausente = `rotulo` + "s". */
  rotuloPlural?: string
  idDe: (item: T) => string
  nomeDe: (item: T) => string
  /** Catalogo de campos: alimenta detalhes E edicao. */
  campos: Array<CampoMeta<T>>
  /** Espelho do Auth: em vez de editar, ressincroniza. */
  ressincroniza?: boolean
  /** `false` esconde ressincronizar, editar e excluir. Ver continua: e leitura. */
  podeGerenciar?: boolean
  /**
   * `false` quando o backend NAO tem `/dependencies` para esta entidade (policies, por exemplo -
   * ver `_DEPENDENCY_ENTITIES` em `routes/catalog.py`). Sem isto a exclusao mostraria um aviso de
   * "nao foi possivel verificar os vinculos" que nao significa nada: nao ha vinculo a verificar.
   */
  verificaDependencias?: boolean
  vazio: ReactNode
  /** Aviso dentro da tela de edicao (regras que o backend impoe). */
  avisoDeEdicao?: ReactNode
}

type Acao<T> = { tipo: 'detalhes' | 'editar' | 'resync' | 'excluir'; item: T }

export function Listagem<T>(props: ListagemProps<T>) {
  const {
    itens,
    colunas,
    entidade,
    chave,
    idDe,
    nomeDe,
    rotulo,
    rotuloPlural,
    campos,
    ressincroniza,
    vazio,
    avisoDeEdicao,
    podeGerenciar = true,
  } = props
  const [acao, setAcao] = useState<Acao<T> | null>(null)
  const queryClient = useQueryClient()
  const editavel = campos.some((campo) => campo.edicao !== undefined)
  const colecao = rotuloPlural ?? `${rotulo}s`

  const salvar = useMutation({
    mutationFn: (entrada: { id: string; mudancas: Record<string, unknown> }) =>
      atualizar(entidade, entrada.id, entrada.mudancas),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [chave] })
      setAcao(null)
    },
  })

  useMarcarRegistroAberto(acao?.tipo === 'detalhes' || acao?.tipo === 'editar')

  // --- pagina inteira: ver e editar ------------------------------------------------------
  if (acao !== null && (acao.tipo === 'detalhes' || acao.tipo === 'editar')) {
    const item = acao.item
    const fechar = () => {
      salvar.reset()
      setAcao(null)
    }

    if (acao.tipo === 'detalhes') {
      return (
        <PaginaDeDetalhes
          aoEditar={
            podeGerenciar && editavel && ressincroniza !== true
              ? () => setAcao({ tipo: 'editar', item })
              : undefined
          }
          aoVoltar={fechar}
          campos={campos}
          colecao={colecao}
          id={idDe(item)}
          item={item}
          rotulo={rotulo}
          titulo={nomeDe(item)}
        />
      )
    }

    return (
      <PaginaDeEdicao
        aoSalvar={(mudancas) => salvar.mutate({ id: idDe(item), mudancas })}
        aoVoltar={fechar}
        aviso={avisoDeEdicao}
        campos={campos}
        colecao={colecao}
        erro={salvar.error === null ? undefined : humanizar(salvar.error)}
        id={idDe(item)}
        item={item}
        rotulo={rotulo}
        salvando={salvar.isPending}
        titulo={nomeDe(item)}
      />
    )
  }

  if (itens.length === 0) return <Alert tone="info">{vazio}</Alert>

  return (
    <>
      <section className="colecao">
        <header className="colecao__cabecalho">
          <h2 className="colecao__titulo">{maiuscula(colecao)}</h2>
          <span className="colecao__contagem">
            {itens.length} {itens.length === 1 ? 'registro' : 'registros'}
          </span>
        </header>

        <div className="tabela">
          <table>
            <thead>
              <tr>
                {colunas.map((coluna) => (
                  <th key={coluna.titulo} scope="col">
                    {coluna.titulo}
                  </th>
                ))}
                <th className="tabela__acoes" scope="col">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item) => (
                <tr key={idDe(item)}>
                  {colunas.map((coluna) => (
                    <td key={coluna.titulo}>{coluna.valor(item)}</td>
                  ))}
                  <td className="tabela__acoes">
                    <div className="acoes">
                      <IconButton
                        icon="list"
                        label={`Ver detalhes de ${nomeDe(item)}`}
                        onClick={() => setAcao({ tipo: 'detalhes', item })}
                      />
                      {podeGerenciar !== true ? null : ressincroniza === true ? (
                        <IconButton
                          icon="refresh"
                          label={`Ressincronizar ${nomeDe(item)} com o Auth`}
                          onClick={() => setAcao({ tipo: 'resync', item })}
                        />
                      ) : editavel ? (
                        <IconButton
                          icon="edit"
                          label={`Editar ${nomeDe(item)}`}
                          onClick={() => setAcao({ tipo: 'editar', item })}
                        />
                      ) : null}
                      {podeGerenciar !== true ? null : (
                        <IconButton
                          icon="trash"
                          label={`Excluir ${nomeDe(item)}`}
                          tone="danger"
                          onClick={() => setAcao({ tipo: 'excluir', item })}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {acao === null ? null : acao.tipo === 'excluir' ? (
        <DialogoExclusao {...props} item={acao.item} aoFechar={() => setAcao(null)} />
      ) : acao.tipo === 'resync' ? (
        <DialogoResync {...props} item={acao.item} aoFechar={() => setAcao(null)} />
      ) : null}
    </>
  )
}

/**
 * GUID abreviado. O valor inteiro fica no `title` e na tela do registro - oito caracteres bastam
 * para reconhecer, mas nao para copiar e colar num chamado.
 */
export function IdCurto({ valor }: { valor: string | null | undefined }) {
  if (valor === null || valor === undefined || valor === '') return <>—</>
  return (
    <code className="id-curto" title={valor}>
      {valor.slice(0, 8)}
    </code>
  )
}

/** Marca de estado: "ativo/sim" em verde, o resto neutro. Cor sozinha nunca carrega o sentido. */
export function Marca({ valor }: { valor: string | boolean | null | undefined }) {
  const texto =
    typeof valor === 'boolean'
      ? valor
        ? 'sim'
        : 'não'
      : valor === null || valor === undefined || valor === ''
        ? '—'
        : valor
  const positivo = texto === 'sim' || texto === 'active' || texto === 'ativo'
  return <StatusBadge tone={positivo ? 'positive' : 'neutral'}>{texto}</StatusBadge>
}

// ------------------------------------------------------- confirmacoes (modais curtos)

function DialogoResync<T>({
  item,
  entidade,
  chave,
  rotulo,
  idDe,
  nomeDe,
  aoFechar,
}: ListagemProps<T> & { item: T; aoFechar: () => void }) {
  const id = useId()
  const queryClient = useQueryClient()
  const mutacao = useMutation({
    mutationFn: () => ressincronizar(entidade, idDe(item)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [chave] })
      aoFechar()
    },
  })

  return (
    <Dialog onRequestClose={aoFechar} open titleId={`${id}-titulo`}>
      <header className="dialogo__cabecalho">
        <h2 id={`${id}-titulo`}>Ressincronizar {rotulo}</h2>
      </header>
      <p>
        Repuxar <strong>nome, slug e status</strong> de <strong>{nomeDe(item)}</strong> direto do
        Auth? Este cadastro é um espelho: a identidade vive no Auth e não é editada aqui.
      </p>
      {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}
      <footer className="dialogo__acoes">
        <Button onClick={aoFechar} variant="ghost">
          Cancelar
        </Button>
        <Button disabled={mutacao.isPending} onClick={() => mutacao.mutate()}>
          {mutacao.isPending ? 'Ressincronizando…' : 'Ressincronizar'}
        </Button>
      </footer>
    </Dialog>
  )
}

function DialogoExclusao<T>({
  item,
  entidade,
  chave,
  rotulo,
  idDe,
  nomeDe,
  verificaDependencias = true,
  aoFechar,
}: ListagemProps<T> & { item: T; aoFechar: () => void }) {
  const id = useId()
  const queryClient = useQueryClient()
  const itemId = idDe(item)

  const dependencias = useQuery({
    queryKey: ['dependencias', entidade, itemId],
    queryFn: () => fetchDependencias(entidade, itemId),
    enabled: verificaDependencias,
    retry: false,
    staleTime: 0,
  })

  const mutacao = useMutation({
    mutationFn: () => excluir(entidade, itemId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [chave] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      aoFechar()
    },
  })

  const bloqueado = dependencias.data?.blocked === true
  const vinculos = dependencias.data?.dependencies ?? []

  return (
    <Dialog onRequestClose={aoFechar} open titleId={`${id}-titulo`}>
      <header className="dialogo__cabecalho">
        <h2 id={`${id}-titulo`}>Excluir {rotulo}</h2>
      </header>
      <p>
        Excluir <strong>{nomeDe(item)}</strong>?
      </p>
      <p className="dialogo__id">{itemId}</p>

      {!verificaDependencias ? (
        <Alert tone="warning">Esta ação não pode ser desfeita.</Alert>
      ) : dependencias.isPending ? (
        <Spinner label="Verificando vínculos…" showLabel />
      ) : dependencias.error !== null ? (
        <Alert tone="warning">
          Não foi possível verificar os vínculos ({humanizar(dependencias.error)}). A exclusão ainda
          será recusada pelo servidor se houver dependências.
        </Alert>
      ) : bloqueado ? (
        <Alert tone="danger">
          Não dá para excluir: este {rotulo} tem{' '}
          {vinculos
            .map((dep) => `${dep.count} ${ROTULOS_DE_DEPENDENCIA[dep.kind] ?? dep.label ?? dep.kind}`)
            .join(', ')}{' '}
          vinculado(s). Exclua os dependentes primeiro.
        </Alert>
      ) : (
        <Alert tone="warning">Esta ação não pode ser desfeita.</Alert>
      )}

      {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}

      <footer className="dialogo__acoes">
        <Button onClick={aoFechar} variant="ghost">
          Cancelar
        </Button>
        <Button
          disabled={bloqueado || mutacao.isPending}
          onClick={() => mutacao.mutate()}
          title={bloqueado ? 'Bloqueado por dependências' : undefined}
          variant="danger"
        >
          {mutacao.isPending ? 'Excluindo…' : 'Excluir'}
        </Button>
      </footer>
    </Dialog>
  )
}
